import { useMemo } from 'react'

import {
  findOffBranchUnstructuredSession,
  pickIngestSessions,
  useActiveBranchRecord,
  useGetAuthzMeQuery,
  useGetIngestJobsPageQuery,
  type UnstructuredIngestSession
} from '@axilon-apps/domains-ontology-ui-state'

import { useIngestSessionAttention } from './useIngestSessionAttention'

/**
 * Must match `SESSION_PRESENCE_PAGE_SIZE` in
 * `state/src/api/ingest/session-presence.ts`.
 */
const SESSION_PRESENCE_PAGE_SIZE = 50

export interface OffBranchUnstructuredNeedsInput {
  session: UnstructuredIngestSession | undefined
  questionId: string | undefined
}

const EMPTY: OffBranchUnstructuredNeedsInput = {
  session: undefined,
  questionId: undefined
}

/**
 * A live unstructured ingest parked on another branch — typically protected
 * `main` — with an open `user_question`. On-branch Current Ingest is skipped
 * for protected branches; this is the handoff that can still offer a swap.
 */
export function useOffBranchUnstructuredNeedsInput(options?: {
  skip?: boolean
}): OffBranchUnstructuredNeedsInput {
  const canReadIngest = useGetAuthzMeQuery().data?.permissions.includes('ingest/read') === true
  const skip = (options?.skip ?? false) || !canReadIngest
  const branch = useActiveBranchRecord()
  const { data } = useGetIngestJobsPageQuery(
    {
      kind: 'unstructured',
      page: 0,
      pageSize: SESSION_PRESENCE_PAGE_SIZE
    },
    {
      skip,
      refetchOnMountOrArgChange: true
    }
  )
  const session = useMemo(
    () => findOffBranchUnstructuredSession(branch, pickIngestSessions(data?.jobs ?? [])),
    [branch, data?.jobs]
  )
  const { pendingQuestionId } = useIngestSessionAttention(session, {
    requestAttention: false
  })

  return useMemo(() => {
    if (skip || !session || !pendingQuestionId) return EMPTY
    return { session, questionId: pendingQuestionId }
  }, [pendingQuestionId, session, skip])
}
